/**
 * "Your statement changed" — what a correction of a series' price per unit did for the client
 * (owner decision S-52). Funds corrected the price per unit of a past cut-off; the client's units
 * and payments were put where the corrected price would have put them, and nothing is asked back.
 * The client reads their own; the staff who serve them read the same card on the client's page.
 * Cash owed below the minimum payment is paid with the client's next payment, or now if the client
 * (or the staff who serve them) asks for it (0195). Shown only when there is something to show.
 */
import { useCallback, useEffect, useState } from 'react';
import portfolioService, { type NavCorrection } from '../../services/portfolio.service';
import authService from '../../services/auth.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDate, formatDateTime, formatMoney } from '../../lib/datetime';
import { colors } from '../../styles/theme';

/** Owed cash nobody has asked for yet: the one case with something to do. */
export function canAskForOwed(c: NavCorrection): boolean {
  return c.cash_status === 'owed_below_de_minimis' && c.owed_settled_with === null;
}

/** What happens to the cash a correction owes the client, in their words. */
export function cashLine(c: NavCorrection): string | null {
  const amount = formatMoney(c.cash_to_client, c.currency_code);
  if (c.payout_status === 'executed') return `${amount} owed to you was paid.`;
  if (c.cash_status === 'payable') return `${amount} owed to you is scheduled to be paid.`;
  if (c.cash_status !== 'owed_below_de_minimis') return null;
  if (c.owed_settled_with === 'next_payment') {
    return `${amount} owed to you is on its way with your latest payment.`;
  }
  if (c.owed_settled_with === 'on_request') {
    return `${amount} owed to you is on its way. Choose the account it goes to where your payouts wait for a destination.`;
  }
  return `${amount} is owed to you and is paid with your next payment. You can also ask for it now.`;
}

export default function NavCorrectionsCard({ clientId }: Readonly<{ clientId?: string }>): JSX.Element | null {
  const [items, setItems] = useState<NavCorrection[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    () => (clientId ? portfolioService.listClientNavCorrections(clientId) : portfolioService.listMyNavCorrections()),
    [clientId],
  );

  useEffect(() => {
    let cancelled = false;
    load()
      .then((rows) => { if (!cancelled) setItems(rows); })
      // Silent: the card appears only when there is a correction to show.
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [load]);

  const askNow = async (c: NavCorrection): Promise<void> => {
    setBusy(c.id);
    setError(null);
    try {
      const owner = clientId ?? (await authService.getMyId());
      await portfolioService.requestNavCorrectionPayout(owner, c.id);
      setItems(await load());
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'We could not ask for it right now. Please try again shortly.'));
    } finally {
      setBusy(null);
    }
  };

  if (items.length === 0) return null;
  return (
    <div
      className="rounded-2xl p-4 mb-4"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <h2 className="text-sm font-semibold mb-1" style={{ color: colors.ink }}>Your statement changed</h2>
      <p className="text-xs mb-3" style={{ color: colors.inkSoft }}>
        The price per unit of a past date was corrected. Your units and payments were put where the corrected price
        would have put them. Nothing is asked back from you.
      </p>
      {error && (
        <div className="mb-3 text-xs" role="alert" style={{ color: colors.danger }}>{error}</div>
      )}
      <ul className="space-y-3">
        {items.map((c) => {
          const cash = cashLine(c);
          return (
            <li key={c.id} className="text-sm" style={{ color: colors.ink }}>
              <div className="font-medium">
                {`${c.product_name}: the price per unit of ${formatDate(c.as_of)} was corrected from ${c.superseded_nav_per_unit} to ${c.corrected_nav_per_unit}.`}
              </div>
              {Number(c.units_issued) > 0 && (
                <div className="text-xs" style={{ color: colors.inkSoft }}>
                  {`${c.units_issued} units were added to your position, worth ${formatMoney(c.value_of_units_issued, c.currency_code)}, at no cost to you.`}
                </div>
              )}
              {cash && <div className="text-xs" style={{ color: colors.inkSoft }}>{cash}</div>}
              {canAskForOwed(c) && (
                <button
                  type="button"
                  onClick={() => { void askNow(c); }}
                  disabled={busy === c.id}
                  className="mt-1 text-xs font-semibold underline"
                  style={{ color: colors.navy }}
                >
                  {busy === c.id ? 'Asking…' : 'Ask for it now'}
                </button>
              )}
              <div className="text-xs" style={{ color: colors.muted }}>{formatDateTime(c.occurred_at)}</div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
